import {
	StepAccordion,
	AccordionMinimumHeight,
	ButtonColorEnum,
	AccordionBackgroundColor,
} from "../../../../Utils/StepAccordion";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import { IAddressAutocompleteResult } from "../../../../../Models/AddressAutocompleteModel";
import { AddressAutocomplete } from "../../../../SharedComponents/AddressAutocomplete/AddressAutocomplete";
import "./AddressStep.scss";
import Column from "../../../../Utils/Column/Column";
import { useContext, useRef, useState } from "react";
import AddressModel, { AddressRequest, toDatahubAddress } from "../../../../../Models/AddressModel";
import { toAddressRequestFromLookup } from "../../../../../Helpers/AddressHelpers";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import img from "../../../../../Assets/Images/Elektricitet-MedVind_colour.png";
import { MobileWrapper } from "../../../../Utils/MobileMargin/MobileMargin";
import { getAddressHasOrderMatch } from "../../../../../Services/ApiService";
import { fetchAddressById } from "../../../../../Services/AddressAutocompleteService";
import { useOrder } from "../../../../../Contexts/OrderContext";

const ADDRESS_VALIDATION_RESET = {
	DatahubValid: false,
	CohabDatahubValid: false,
	InDelivery: false,
	InBinding: false,
	existingCustomer: false,
};

const AddressStep = ({
	nextStep,
	setAddress,
	open,
	toggleOpen,
	setErrorMode,
}: IStepInterface) => {
	const { getOrder, updateOrder } = useOrder();
	const [addressValid, setAddressValid] = useState(false);
	const [lookupPending, setLookupPending] = useState(false);
	const [lookupError, setLookupError] = useState(false);
	const lookupRequestId = useRef(0);
	const selectedAddressId = useRef<string | null>(null);
	const resolvedAddressId = useRef<string | null>(null);
	const autocompleteValid = useRef(false);
	const advancedAddressId = useRef<string | null>(null);
	const partnerContentContext = useContext(PartnerContentContext);
	const [keyboardPadding, setKeyboardPadding] = useState<number>(0);
	const DOMPurify = require("dompurify")(window);

	const clearAddress = () => {
		lookupRequestId.current += 1;
		selectedAddressId.current = null;
		resolvedAddressId.current = null;
		autocompleteValid.current = false;
		advancedAddressId.current = null;
		setAddressValid(false);
		setLookupPending(false);
		setLookupError(false);
		setAddress(new AddressModel());
		updateOrder({
			...ADDRESS_VALIDATION_RESET,
			deliveryAddress: "",
			deliveryAddressId: "",
			addressRequest: new AddressRequest(),
		});
	};

	const advanceIfReady = (addressId: string) => {
		if (
			selectedAddressId.current === addressId &&
			resolvedAddressId.current === addressId &&
			autocompleteValid.current &&
			advancedAddressId.current !== addressId
		) {
			advancedAddressId.current = addressId;
			setAddressValid(true);
			nextStep();
		}
	};

	const resolveSelectedAddress = async (addressId: string) => {
		const requestId = ++lookupRequestId.current;
		try {
			const details = await fetchAddressById(addressId);
			if (
				requestId !== lookupRequestId.current ||
				selectedAddressId.current !== addressId ||
				getOrder().deliveryAddressId !== addressId
			) {
				return;
			}
			setLookupPending(false);

			const addressRequest = details?.id === addressId
				? toAddressRequestFromLookup(details)
				: null;
			if (!details || !addressRequest) {
				clearAddress();
				setLookupError(true);
				return;
			}

			setAddress(toDatahubAddress(addressRequest, details));
			updateOrder({ addressRequest });
			resolvedAddressId.current = addressId;
			setLookupError(false);
			advanceIfReady(addressId);
		} catch {
			if (
				requestId === lookupRequestId.current &&
				selectedAddressId.current === addressId &&
				getOrder().deliveryAddressId === addressId
			) {
				clearAddress();
				setLookupError(true);
			}
		}
	};

	const handleAddressChange = (val: IAddressAutocompleteResult): any => {
		if (val.valid) {
			const addressId = val.value.adresse.id.toString();
			selectedAddressId.current = addressId;
			resolvedAddressId.current = null;
			autocompleteValid.current = false;
			advancedAddressId.current = null;
			setAddressValid(false);
			setLookupPending(true);
			setLookupError(false);
			setAddress(new AddressModel());
			updateOrder({
				...ADDRESS_VALIDATION_RESET,
				deliveryAddress: val.value.tekst,
				deliveryAddressId: addressId,
				addressRequest: new AddressRequest(),
			});
			resolveSelectedAddress(addressId);
			getAddressHasOrderMatch(addressId).then((result) => {
				if (getOrder().deliveryAddressId === addressId && result.isSuccessful && result.data) {
					setErrorMode("ExistingCustomer");
					return false;
				}
			});
		} else {
			clearAddress();
		}
	};

	const validAddress = (isValid: boolean): boolean => {
		if (!isValid) {
			clearAddress();
			return false;
		}

		const addressId = selectedAddressId.current;
		if (!addressId) return false;
		autocompleteValid.current = true;
		advanceIfReady(addressId);
		return resolvedAddressId.current === addressId;
	};

	const formValid = () => {
		return addressValid;
	};

	const handleTouchStart = () => {
		setKeyboardPadding(55);
	};

	return (
		<div>
			<div className="address-step-desktop">
				<StepAccordion
					label={partnerContentContext.addressStep.title}
					backgroundColor={AccordionBackgroundColor.GREY}
					open={open}
					click={toggleOpen}
					secondButtonType={formValid() ? ButtonColorEnum.DARK_GREEN : ButtonColorEnum.GREY}
					secondButtonDisabled={!formValid()}
					minimumHeight={AccordionMinimumHeight.EXTRASMALL}
				>
					<Column>
						<AddressAutocomplete
							validAddress={validAddress}
							placeholder={
								partnerContentContext.addressStep.placeHolder
									? partnerContentContext.addressStep.placeHolder
									: "Leveringsadresse"
							}
							change={handleAddressChange}
						/>
						{lookupError && <p role="alert">Kunne ikke hente adresseoplysninger. Vælg adressen igen.</p>}
							{lookupPending && <p aria-live="polite">Henter adresseoplysninger...</p>}
					</Column>
				</StepAccordion>
			</div>
			<div className="address-step-mobile">
				<div className="address-mobile-container">
					<h1>{partnerContentContext.heroBanner.title}</h1>

					<div>
						<p className="banner-text">
							<b>{partnerContentContext.heroBanner.boldText}</b>
						</p>

						<div
							className="address-text-section"
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(partnerContentContext.heroBanner.text),
							}}>
						</div>
					</div>

					<Column>
						<AddressAutocomplete
							touchStart={handleTouchStart}
							validAddress={validAddress}
							placeholder={
								partnerContentContext.addressStep.placeHolder
									? partnerContentContext.addressStep.placeHolder
									: "Leveringsadresse"
							}
							change={handleAddressChange}
						/>
						{lookupError && <p role="alert">Kunne ikke hente adresseoplysninger. Vælg adressen igen.</p>}
						{lookupPending && <p aria-live="polite">Henter adresseoplysninger...</p>}
					</Column>

					<img
						alt="medvind"
						src={partnerContentContext.heroBanner.image ? partnerContentContext.heroBanner.image : img}
					/>

					<MobileWrapper marginTopViewheight={keyboardPadding} />
				</div>
			</div>
		</div>
	);
};

export default AddressStep;
